/**
 * AWAD — Carrito lateral de PROMSA (tema "AS | CARRITO LATERAL | TEST").
 *
 * Qué hace:
 *  1. Boleta o factura dentro del carrito lateral: muestra los campos solo cuando el
 *     cliente elige factura, formatea y valida el RUT chileno, y guarda todo como
 *     atributos del carrito antes de mandar a pagar.
 *  2. Deja el botón de pagar como único camino: intercepta el envío del formulario,
 *     guarda los datos con /cart/update.js y recién ahí va a /checkout.
 *  3. Abre el carrito lateral solo cuando alguien llega con #carrito en la URL
 *     (es lo que usa la página /cart, que ahora es solo un puente).
 *
 * Dos cosas que hay que tener en la cabeza al tocar este archivo:
 *  - El tema REEMPLAZA el HTML del carrito entero cada vez que cambia el carro, así que
 *    todo va con delegación de eventos en `document` y el estado se rescata de
 *    sessionStorage al redibujar. Enganchar un listener a un nodo concreto no sirve.
 *  - Los nombres de los atributos (shoppingcart-tags, RUT, razon, giro, direccionFiscal)
 *    son los que lee Bsale para emitir el documento. No se cambian.
 */
(() => {
  const LLAVE = 'as-promsa-doc';
  const CAMPOS = {
    rut: 'RUT',
    razon: 'razon',
    giro: 'giro',
    direccion: 'direccionFiscal',
  };
  const ETIQUETAS = {
    rut: 'el RUT',
    razon: 'la razón social',
    giro: 'el giro',
    direccion: 'la dirección de facturación',
  };

  /* ------------------------------------------------------------------
     RUT chileno
     ------------------------------------------------------------------ */

  /** Deja solo números y la K final. */
  const limpiarRut = (valor) => (valor || '').toString().toUpperCase().replace(/[^0-9K]/g, '');

  /** 12345678-5 → 12.345.678-5 */
  const formatearRut = (valor) => {
    const limpio = limpiarRut(valor);
    if (limpio.length < 2) return limpio;
    const cuerpo = limpio.slice(0, -1);
    const dv = limpio.slice(-1);
    return `${cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}`;
  };

  /** Valida el dígito verificador con el módulo 11. */
  const rutValido = (valor) => {
    const limpio = limpiarRut(valor);
    if (limpio.length < 8) return false;
    const cuerpo = limpio.slice(0, -1);
    const dv = limpio.slice(-1);
    if (!/^\d+$/.test(cuerpo)) return false;

    let suma = 0;
    let multiplo = 2;
    for (let i = cuerpo.length - 1; i >= 0; i -= 1) {
      suma += parseInt(cuerpo[i], 10) * multiplo;
      multiplo = multiplo === 7 ? 2 : multiplo + 1;
    }
    const resto = 11 - (suma % 11);
    const esperado = resto === 11 ? '0' : resto === 10 ? 'K' : String(resto);
    return esperado === dv;
  };

  /* ------------------------------------------------------------------
     Estado (sobrevive a que el tema redibuje el carrito)

     Ojo con la diferencia:
     - Los TEXTOS de la factura van a sessionStorage: si el cliente sale y vuelve, no
       tiene que escribir el RUT de nuevo.
     - La ELECCIÓN boleta/factura vive solo en memoria, mientras la página esté abierta.
       Así el carrito siempre se abre con las dos opciones sin marcar (decisión de Juako,
       2-sep-2026) pero no se pierde la elección cuando el tema redibuja el carrito al
       agregar o quitar un producto.
     ------------------------------------------------------------------ */

  let tipoDeEstaVisita = '';

  const leerEstado = () => {
    try {
      return JSON.parse(sessionStorage.getItem(LLAVE)) || {};
    } catch (e) {
      return {};
    }
  };

  const guardarEstado = (estado) => {
    try {
      sessionStorage.setItem(LLAVE, JSON.stringify(estado));
    } catch (e) {
      /* modo incógnito con almacenamiento bloqueado: se sigue igual */
    }
  };

  const bloque = () => document.querySelector('[data-as-doc]');
  const campo = (nombre) => document.querySelector(`[data-as-doc-campo="${nombre}"]`);

  // Devuelve '' cuando el cliente todavía no eligió. A propósito: ninguna de las dos
  // opciones viene marcada, para que nadie reciba una boleta necesitando factura.
  const tipoElegido = () => {
    const marcado = document.querySelector('[data-as-doc-tipo]:checked');
    return marcado ? marcado.value : '';
  };

  /* ------------------------------------------------------------------
     Pintar
     ------------------------------------------------------------------ */

  const pintarTipo = () => {
    const caja = bloque();
    if (!caja) return;
    const tipo = tipoElegido();
    caja.dataset.tipo = tipo;

    caja.querySelectorAll('.as-doc__opcion').forEach((opcion) => {
      const radio = opcion.querySelector('input[type="radio"]');
      opcion.classList.toggle('is-selected', !!radio && radio.checked);
    });

    const campos = caja.querySelector('[data-as-doc-campos]');
    if (campos) campos.hidden = tipo !== 'factura';
    if (tipo !== 'factura') limpiarErrores();
  };

  /**
   * Lleva el carrito lateral hasta un elemento, sin salto brusco.
   * Se desplaza el contenedor del carrito, no la página de atrás.
   * @param {HTMLElement} elemento
   */
  const desplazarHasta = (elemento) => {
    if (!elemento) return;
    const contenedor = document.querySelector('#cart-drawer .drawer__content');
    if (!contenedor) {
      elemento.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const cajaContenedor = contenedor.getBoundingClientRect();
    const cajaElemento = elemento.getBoundingClientRect();
    const destino = contenedor.scrollTop
      + (cajaElemento.top - cajaContenedor.top)
      - contenedor.clientHeight / 2
      + cajaElemento.height / 2;
    contenedor.scrollTo({ top: Math.max(0, destino), behavior: 'smooth' });
  };

  const mostrarError = (nombre, texto) => {
    const aviso = document.querySelector(`[data-as-doc-error="${nombre}"]`);
    const entrada = campo(nombre);
    const opciones = nombre === 'tipo' ? document.querySelector('.as-doc__opciones') : null;
    if (opciones) opciones.classList.toggle('as-doc__opciones--error', !!texto);
    if (entrada) {
      entrada.classList.toggle('is-invalido', !!texto);
      entrada.classList.toggle('is-valido', !texto && !!entrada.value.trim());
      const caja = entrada.closest('.as-doc__campo');
      if (caja) caja.classList.toggle('as-doc__campo--error', !!texto);
    }
    if (!aviso) return;
    aviso.textContent = texto || '';
    aviso.hidden = !texto;
  };

  // Ojo: acá NO se toca el aviso de arriba del botón. Esta función la llama pintarTipo(),
  // que a su vez corre cada vez que el tema redibuja el carrito; si borrara el aviso, el
  // mensaje de "completa los datos" o "marca los términos" se apagaría solo al instante.
  const limpiarErrores = () => {
    Object.keys(CAMPOS).forEach((nombre) => mostrarError(nombre, ''));
  };

  /** Devuelve los datos escritos; si el carrito se redibujó, los repone. */
  const restaurar = () => {
    const caja = bloque();
    if (!caja) return;
    const estado = leerEstado();

    if (tipoDeEstaVisita) {
      const radio = document.querySelector(`[data-as-doc-tipo][value="${tipoDeEstaVisita}"]`);
      if (radio) radio.checked = true;
    }

    Object.keys(CAMPOS).forEach((nombre) => {
      const entrada = campo(nombre);
      if (entrada && !entrada.value && estado[nombre]) entrada.value = estado[nombre];
    });

    pintarTipo();
  };

  const recordar = () => {
    tipoDeEstaVisita = tipoElegido();
    const estado = {};
    Object.keys(CAMPOS).forEach((nombre) => {
      const entrada = campo(nombre);
      estado[nombre] = entrada ? entrada.value.trim() : '';
    });
    guardarEstado(estado);
  };

  /* ------------------------------------------------------------------
     Validación antes de pagar
     ------------------------------------------------------------------ */

  const validar = () => {
    const tipo = tipoElegido();

    if (!tipo) {
      mostrarError('tipo', 'Debes marcar una de las dos opciones: boleta o factura.');
      const caja = bloque();
      return caja ? caja.querySelector('.as-doc__opciones') : null;
    }

    mostrarError('tipo', '');
    if (tipo !== 'factura') return null;

    let primerError = null;

    Object.keys(CAMPOS).forEach((nombre) => {
      const entrada = campo(nombre);
      if (!entrada) return;
      const valor = entrada.value.trim();

      if (!valor) {
        mostrarError(nombre, `Falta ${ETIQUETAS[nombre]}.`);
        if (!primerError) primerError = entrada;
        return;
      }

      if (nombre === 'rut' && !rutValido(valor)) {
        mostrarError(nombre, 'Ese RUT no es válido. Revisa el número y el dígito después del guion.');
        if (!primerError) primerError = entrada;
        return;
      }

      mostrarError(nombre, '');
    });

    return primerError;
  };

  /* ------------------------------------------------------------------
     Guardar en el carrito y pagar
     ------------------------------------------------------------------ */

  const guardarEnCarrito = async () => {
    const tipo = tipoElegido();
    const attributes = { 'shoppingcart-tags': tipo };

    Object.entries(CAMPOS).forEach(([nombre, atributo]) => {
      const entrada = campo(nombre);
      // Con boleta se mandan vacíos a propósito: así se borran los datos de una
      // factura que el cliente empezó a llenar y después descartó.
      attributes[atributo] = tipo === 'factura' && entrada ? entrada.value.trim() : '';
    });

    const respuesta = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });

    if (!respuesta.ok) throw new Error('No se pudieron guardar los datos del documento');
    return respuesta.json();
  };

  const avisarPago = (texto) => {
    const aviso = document.querySelector('[data-as-pagar-aviso]');
    if (!aviso) return;
    aviso.textContent = texto || '';
    aviso.hidden = !texto;
  };

  /* ------------------------------------------------------------------
     Eventos (delegados: el carrito se redibuja entero)
     ------------------------------------------------------------------ */

  document.addEventListener('change', (evt) => {
    const objetivo = evt.target;
    if (!objetivo) return;

    if (objetivo.matches('[data-as-doc-tipo]')) {
      pintarTipo();
      recordar();
      avisarPago('');
      mostrarError('tipo', '');
      if (objetivo.value === 'factura') {
        // Los campos aparecen abajo del todo: hay que llevarlos a la vista o el cliente
        // cree que no pasó nada.
        const caja = bloque();
        setTimeout(() => desplazarHasta(caja), 50);
        const rut = campo('rut');
        if (rut) setTimeout(() => rut.focus({ preventScroll: true }), 400);
      } else {
        const caja = bloque();
        if (caja) caja.classList.remove('as-doc--error');
      }
      return;
    }

    if (objetivo.matches('[data-as-doc-campo]')) {
      const nombre = objetivo.dataset.asDocCampo;
      // El RUT se formatea recién al salir del campo: si se hace mientras escribe,
      // el cursor salta solo cada vez que se agrega un punto.
      if (nombre === 'rut' && objetivo.value.trim()) objetivo.value = formatearRut(objetivo.value);
      if (objetivo.value.trim()) mostrarError(nombre, '');
      recordar();
    }
  });

  document.addEventListener('input', (evt) => {
    if (evt.target && evt.target.matches('[data-as-doc-campo]')) {
      mostrarError(evt.target.dataset.asDocCampo, '');
      avisarPago('');
      const caja = bloque();
      if (caja && !caja.querySelector('.as-doc__campo--error')) caja.classList.remove('as-doc--error');
    }
  });

  // Flechas del carrusel de accesorios. Se mueve de a una tarjeta: el ancho lo dice la
  // primera, así no hay que repetir la medida en el CSS y en el JavaScript.
  document.addEventListener('click', (evt) => {
    const flecha = evt.target.closest && evt.target.closest('[data-as-rel-mover]');
    if (!flecha) return;
    const bloqueRel = flecha.closest('[data-as-rel]');
    const pista = bloqueRel && bloqueRel.querySelector('[data-as-rel-pista]');
    if (!pista) return;
    const tarjeta = pista.querySelector('.as-rel__item');
    const paso = tarjeta ? tarjeta.getBoundingClientRect().width + 10 : pista.clientWidth;
    pista.scrollBy({ left: paso * Number(flecha.dataset.asRelMover), behavior: 'smooth' });
  });

  document.addEventListener('submit', async (evt) => {
    const form = evt.target;
    if (!form || form.id !== 'cart-drawer-form') return;
    if (!bloque()) return; // sin el bloque de documento, que el tema haga lo suyo

    // Si otro código ya frenó el envío (la app de "acepto los términos y condiciones"
    // lo hace cuando el cliente no marcó la casilla), acá no se sigue: sería saltarse
    // su validación y mandar al pago igual.
    if (evt.defaultPrevented) return;

    evt.preventDefault();

    const error = validar();
    if (error) {
      avisarPago(tipoElegido()
        ? 'Antes de pagar, completa los datos de la factura.'
        : 'Antes de pagar, marca si necesitas boleta o factura.');
      const caja = bloque();
      if (caja) caja.classList.add('as-doc--error');
      // Primero se sube hasta el campo que falta y después se le da el foco: si se hace
      // al revés, el navegador salta de golpe y el cliente no ve de dónde vino.
      desplazarHasta(error.closest('.as-doc__campo') || error);
      const aEnfocar = error.matches('.as-doc__opciones') ? error.querySelector('input') : error;
      if (aEnfocar) setTimeout(() => aEnfocar.focus({ preventScroll: true }), 350);
      return;
    }

    const boton = form.querySelector('[name="checkout"]') || document.querySelector('[name="checkout"][form="cart-drawer-form"]');
    const textoOriginal = boton ? boton.innerHTML : '';
    if (boton) {
      boton.disabled = true;
      boton.innerHTML = 'Llevándote al pago…';
    }

    try {
      await guardarEnCarrito();
      window.location.href = '/checkout';
    } catch (e) {
      if (boton) {
        boton.disabled = false;
        boton.innerHTML = textoOriginal;
      }
      avisarPago('No pudimos guardar tus datos. Inténtalo de nuevo.');
    }
  });

  /* ------------------------------------------------------------------
     Arranque y redibujos
     ------------------------------------------------------------------ */

  // El observador se apaga mientras restauramos: restaurar() también toca el DOM
  // (clases y textos de error) y, si no, se llamaría a sí mismo en bucle.
  let observador = null;
  const AJUSTES_OBSERVADOR = { childList: true, subtree: true };

  const restaurarSeguro = () => {
    if (observador) observador.disconnect();
    restaurar();
    const carrito = document.getElementById('cart-drawer');
    if (observador && carrito) observador.observe(carrito, AJUSTES_OBSERVADOR);
  };

  const abrirSiLoPiden = () => {
    if (window.location.hash !== '#carrito') return;
    // Se saca el #carrito de la URL para que al recargar no se vuelva a abrir solo.
    window.history.replaceState({}, '', window.location.pathname + window.location.search);

    const abrir = () => {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      // Respaldo: si el componente del carrito todavía no estaba listo, se aprieta
      // el ícono del carrito, que es lo mismo que haría el cliente.
      setTimeout(() => {
        const carrito = document.getElementById('cart-drawer');
        if (carrito && carrito.getAttribute('aria-hidden') === 'false') return;
        const icono = document.querySelector('.js-cart-icon, [href$="/cart"]');
        if (icono) icono.click();
      }, 400);
    };

    if (document.readyState === 'complete') {
      abrir();
    } else {
      window.addEventListener('load', abrir, { once: true });
    }
  };

  const iniciar = () => {
    restaurar();
    abrirSiLoPiden();

    // El tema reemplaza el HTML del carrito al agregar o quitar productos: hay que
    // volver a poner lo que el cliente ya había escrito.
    const carrito = document.getElementById('cart-drawer');
    if (!carrito) return;
    observador = new MutationObserver(() => {
      window.requestAnimationFrame(restaurarSeguro);
    });
    observador.observe(carrito, AJUSTES_OBSERVADOR);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar);
  } else {
    iniciar();
  }
})();
